import * as React from "react";
import { useFormikContext } from "formik";
import { Alert, Button, Checkbox, Field, RadioGroup } from "@/components/ui";
import { SmartLink, SmartLinkServices } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { AlertTriangle, Info, Plus, WandSparkles } from "lucide-react";
import { URLS, useAccountSettings, useCoachSettings, useSocialCoachSession } from "@/utils";
import { CoachSettings, Media, MediaGroup, Prompt, WebSettings } from "@/interfaces";
import { SmartLinkCard } from "@/containers/Coach/SmartLink/Card/SmartLinkCard";
import { SmartLinkDialog } from "@/containers/Coach/SmartLink/Dialog/SmartLinkDialog";
import { SmartLinkStatsDialog } from "@/containers/Coach/SmartLink/StatsDialog/SmartLinkStatsDialog";
import { SmartLinkAggregatedStatsDialog } from "@/containers/Coach/SmartLink/StatsDialog/SmartLinkAggregatedStatsDialog";

interface Props {
  prompt?: Prompt;
  values: {
    allowHashtags: boolean;
    allowComplianceDisclaimer: boolean;
    allowShortLink: boolean;
    linkType: "PLAYER_LINK" | "SMART_LINK";
    smartLinkId: number;
    post?: MediaGroup;
  };
  handleSetFieldValue?: (field: string, value: any) => void;
  disabled: boolean;
}

export function CaptionSettings({
  prompt,
  values: { allowHashtags, allowComplianceDisclaimer, allowShortLink, linkType, smartLinkId, post },
  disabled,
  handleSetFieldValue,
}: Props) {
  const { validateField } = useFormikContext();
  const { coachId } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings();
  const { hasSetting } = useCoachSettings(coachId);
  const hasSmartLinks = hasAccountSetting(WebSettings.LINK_TRACKING);
  const showHashtagToggle = React.useMemo(() => hasSetting(CoachSettings.PLAYER_HASHTAG, true), [hasSetting]);
  const disclaimerOnEveryPost = hasSetting(CoachSettings.COMPLIANCE_DISCLAIMER, false);
  const originalMedia: Media | undefined = post?.media?.[0];
  const [showSmartLinkStats, setShowSmartLinkStats] = React.useState<boolean>(false);

  const [isSmartLinkDialogOpen, setIsSmartLinkDialogOpen] = React.useState(false);
  const [editingSmartLink, setEditingSmartLink] = React.useState<SmartLink | undefined>();

  const [selectedSmartLink, setSelectedSmartLink] = React.useState<SmartLink | undefined>();

  React.useEffect(() => {
    if (smartLinkId) {
      SmartLinkServices.getSmartLink(smartLinkId).then(setSelectedSmartLink);
    }
  }, [smartLinkId]);

  return (
    <>
      <Field.Set>
        <div>
          <Field.Label>Caption Personalization</Field.Label>
          <Field.Description>Control the elements automatically added to the caption.</Field.Description>
        </div>

        <Field.Group className="flex flex-col gap-4 border-l-3 border-slate-200 ml-4 p-4">
          {showHashtagToggle && (
            <Field orientation="horizontal">
              <Checkbox
                id={"allowHashtags"}
                disabled={disabled}
                className="border-slate-500"
                checked={allowHashtags}
                onCheckedChange={checked => {
                  handleSetFieldValue!("allowHashtags", checked);
                }}
              />
              <Field.Content>
                <Field.Label htmlFor={"allowHashtags"}>Add Player Hashtags</Field.Label>
                <Field.Description>Include the Player's saved hashtags.</Field.Description>
              </Field.Content>
            </Field>
          )}
          <Field orientation="horizontal">
            <Checkbox
              id={"allowComplianceDisclaimer"}
              disabled={disabled}
              className="border-slate-500"
              checked={allowComplianceDisclaimer}
              onCheckedChange={checked => {
                handleSetFieldValue!("allowComplianceDisclaimer", checked);
              }}
            />
            <Field.Content>
              <Field.Label htmlFor={"allowComplianceDisclaimer"}>Add Compliance Disclaimer</Field.Label>
              <Field.Description>Place the Player's compliance disclaimer at the bottom of the post.</Field.Description>
              {disclaimerOnEveryPost && (
                <Alert variant="warning" className="mt-1">
                  <AlertTriangle />
                  <Alert.Description>
                    <p>
                      The disclaimer is always added. Your Platform Settings include it on every Player post.{" "}
                      <a
                        href={URLS.coach.platformSettings}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline"
                      >
                        Change in Platform Settings
                      </a>
                    </p>
                  </Alert.Description>
                </Alert>
              )}
            </Field.Content>
          </Field>
          <Field orientation="horizontal">
            <Checkbox
              id={"allowShortLink"}
              disabled={disabled}
              className="border-slate-500"
              checked={allowShortLink}
              onCheckedChange={checked => {
                handleSetFieldValue!("allowShortLink", checked);
              }}
            />
            <Field.Content>
              <Field.Label htmlFor={"allowShortLink"}>Add a Link</Field.Label>
              <Field.Description>Select the destination for this post:</Field.Description>

              <div className={cn("flex flex-col gap-2", !allowShortLink ? "opacity-40 pointer-events-none" : "")}>
                <div className={"text-sm text-muted-foreground flex flex-row items-center gap-1"}>
                  <Info className="size-4 text-slate-400" />
                  Note: Links will be automatically omitted from Instagram posts.
                </div>
                <RadioGroup
                  value={linkType}
                  disabled={disabled || !allowShortLink}
                  onValueChange={v => {
                    handleSetFieldValue!("linkType", v);
                    if (v === "PLAYER_LINK") {
                      handleSetFieldValue!("smartLinkId", undefined);
                      setTimeout(() => validateField("smartLinkId"), 0);
                    } else if (v === "SMART_LINK" && !smartLinkId) {
                      setIsSmartLinkDialogOpen(true);
                    }
                  }}
                >
                  <Field.Label htmlFor="player-link" className={linkType !== "PLAYER_LINK" ? "border-slate-300" : ""}>
                    <Field orientation="horizontal">
                      <Field.Content>
                        <Field.Title>Player Link</Field.Title>
                        <Field.Description>Route traffic to the Player's website or Player Card.</Field.Description>
                      </Field.Content>
                      <RadioGroup.Item value="PLAYER_LINK" id="player-link" />
                    </Field>
                  </Field.Label>
                  {hasSmartLinks ? (
                    <Field.Label
                      htmlFor="smart-link"
                      className={cn("flex flex-col gap-2", linkType !== "SMART_LINK" ? "border-slate-300" : "")}
                    >
                      <Field orientation="horizontal">
                        <Field.Content>
                          <Field.Title>Smart Link</Field.Title>
                          <Field.Description>
                            Route traffic to a specific trackable campaign asset (e.g., a blog post or landing page).
                          </Field.Description>
                        </Field.Content>
                        <RadioGroup.Item value="SMART_LINK" id="smart-link" />
                      </Field>

                      {linkType === "SMART_LINK" && (
                        <div className="flex flex-col gap-2 w-full px-2 pb-2" onClick={e => e.stopPropagation()}>
                          {selectedSmartLink ? (
                            <SmartLinkCard
                              smartLink={selectedSmartLink}
                              onStatsClick={() => setShowSmartLinkStats(true)}
                              onEditClick={() => {
                                setEditingSmartLink(selectedSmartLink);
                                setIsSmartLinkDialogOpen(true);
                              }}
                            />
                          ) : (
                            <Button
                              variant="outline"
                              className="w-full justify-start text-muted-foreground font-normal h-12 border-dashed"
                              type="button"
                              onClick={() => {
                                setEditingSmartLink(undefined);
                                setIsSmartLinkDialogOpen(true);
                              }}
                            >
                              <Plus className="size-4 mr-2" />
                              Select a Smart Link
                            </Button>
                          )}
                        </div>
                      )}
                    </Field.Label>
                  ) : (
                    <div className="p-5 border border-slate-200 rounded-xl bg-slate-50/50 flex flex-col gap-3 mt-2 border-l-4 border-l-primary shadow-sm">
                      <div className="flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider">
                        <WandSparkles className="size-4" />
                        Unlock Smart Links
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground leading-relaxed">
                          Route traffic to specific campaign assets—like blog posts or landing pages.
                        </p>
                        <p className="text-sm font text-muted-foreground leading-relaxed">
                          Smart Links automatically inject UTM parameters for Google Analytics, while giving you native
                          dashboards to track click performance across campaigns and social networks.
                        </p>
                      </div>

                      <div className="text-sm text-slate-500 font-medium pt-4 border-t border-slate-200/60">
                        Interested? Reach out to{" "}
                        <a href="mailto:support@socialcoach.io" className="text-primary text-sm hover:underline">
                          support@socialcoach.io
                        </a>{" "}
                        to enable Smart Links for your team.
                      </div>
                    </div>
                  )}
                </RadioGroup>
              </div>
            </Field.Content>
          </Field>
        </Field.Group>
      </Field.Set>
      {isSmartLinkDialogOpen && (
        <SmartLinkDialog
          open={isSmartLinkDialogOpen}
          onOpenChange={setIsSmartLinkDialogOpen}
          smartLink={editingSmartLink}
          altThumbnail={originalMedia?.category == "IMAGE" ? originalMedia?.uri : undefined}
          onSuccess={newLink => {
            setSelectedSmartLink(newLink);
            handleSetFieldValue!("smartLinkId", newLink.id);
          }}
          onCancel={() => {
            setIsSmartLinkDialogOpen(false);
          }}
        />
      )}
      {showSmartLinkStats && selectedSmartLink && (
        <>
          {prompt ? (
            <SmartLinkAggregatedStatsDialog
              open={showSmartLinkStats}
              onOpenChange={setShowSmartLinkStats}
              type={"Prompt"}
              entity={prompt}
              smartLink={selectedSmartLink}
            />
          ) : (
            <SmartLinkStatsDialog
              smartLink={selectedSmartLink}
              open={showSmartLinkStats}
              onOpenChange={setShowSmartLinkStats}
            />
          )}
        </>
      )}
    </>
  );
}
